import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BaseEntity,
  FetchEntitiesFn,
} from "@/components/EntityPickerModal/EntityPickerModal";
import { FilterConfig } from "@/components/ChipFilterGroup/ChipFilterGroup";
import { getBillingInvoices } from "../api/getBillingInvoices/getBillingInvoicesApi";
import { BillingInvoice } from "../api/getBillingInvoices/types";
import { useBillingClientTable } from "./useBillingClientTable";
import { useBillingInvoiceDrawer } from "./useBillingInvoiceDrawer";
import { useBillingInvoicePdfDownload } from "./useBillingInvoicePdfDownload";
import { INVOICE_SORT_ACCESSORS } from "../config/billingInvoiceColumns";
import { BILLING_INVOICE_STATUS_LABELS } from "../config/billingInvoiceStatuses";
import { formatBillingSubscriptionName } from "../utils/billingFormatters";

const SUBSCRIPTION_FILTER_KEY = "subscription";
const STATUS_FILTER_KEY = "status";

const STATUS_OPTIONS: BaseEntity[] = Object.entries(
  BILLING_INVOICE_STATUS_LABELS,
)
  .map(([id, name]) => ({ id, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

function buildSubscriptionOptions(invoices: BillingInvoice[]): BaseEntity[] {
  const optionsBySubscriptionId = new Map<string, BaseEntity>();

  invoices.forEach(({ subscription }) => {
    if (!subscription || optionsBySubscriptionId.has(subscription.id)) return;

    optionsBySubscriptionId.set(subscription.id, {
      id: subscription.id,
      name: formatBillingSubscriptionName(
        subscription.productName,
        subscription.trade,
      ),
    });
  });

  return Array.from(optionsBySubscriptionId.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

function createLocalFetchEntities(
  options: BaseEntity[],
): FetchEntitiesFn<BaseEntity> {
  return async ({ searchTerm }) => {
    const term = searchTerm.trim().toLowerCase();
    const data = term
      ? options.filter((option) => option.name.toLowerCase().includes(term))
      : options;

    return { data, totalCount: data.length };
  };
}

function hasSelectedId(
  selected: BaseEntity[],
  id: string | undefined,
): boolean {
  return selected.some((entity) => entity.id === id);
}

function matchesFilters(
  invoice: BillingInvoice,
  subscriptions: BaseEntity[],
  statuses: BaseEntity[],
): boolean {
  if (
    subscriptions.length > 0 &&
    !hasSelectedId(subscriptions, invoice.subscription?.id)
  ) {
    return false;
  }

  return statuses.length === 0 || hasSelectedId(statuses, invoice.status);
}

export function useBillingInvoicesPage() {
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<BaseEntity[]>([]);
  const [statuses, setStatuses] = useState<BaseEntity[]>([]);
  const invoiceDrawer = useBillingInvoiceDrawer();
  const invoicePdfDownload = useBillingInvoicePdfDownload();

  useEffect(() => {
    const controller = new AbortController();

    getBillingInvoices(controller.signal)
      .then((response) => {
        setInvoices(response.data);
        setLoading(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const filterConfigs: FilterConfig<BaseEntity>[] = useMemo(
    () => [
      {
        key: SUBSCRIPTION_FILTER_KEY,
        type: "multi",
        label: "Subscription",
        color: "gray",
        entityNameSingular: "Subscription",
        entityNamePlural: "Subscriptions",
        fetchEntities: createLocalFetchEntities(
          buildSubscriptionOptions(invoices),
        ),
      },
      {
        key: STATUS_FILTER_KEY,
        type: "multi",
        label: "Status",
        color: "gray",
        entityNameSingular: "Status",
        entityNamePlural: "Statuses",
        fetchEntities: createLocalFetchEntities(STATUS_OPTIONS),
      },
    ],
    [invoices],
  );

  const filterValues = useMemo(
    () => ({
      [SUBSCRIPTION_FILTER_KEY]: subscriptions,
      [STATUS_FILTER_KEY]: statuses,
    }),
    [subscriptions, statuses],
  );

  const filteredInvoices = useMemo(
    () =>
      invoices.filter((invoice) =>
        matchesFilters(invoice, subscriptions, statuses),
      ),
    [invoices, subscriptions, statuses],
  );

  const {
    sorting,
    handleSortingChange,
    pageIndex,
    pageSize,
    pageRows,
    handlePageChange,
    resetToFirstPage,
  } = useBillingClientTable(filteredInvoices, INVOICE_SORT_ACCESSORS);

  const handleFilterChange = useCallback(
    (key: string, value: BaseEntity[] | BaseEntity | null) => {
      const selected = Array.isArray(value) ? value : [];

      if (key === SUBSCRIPTION_FILTER_KEY) setSubscriptions(selected);
      if (key === STATUS_FILTER_KEY) setStatuses(selected);

      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const handleClearFilters = useCallback(() => {
    setSubscriptions([]);
    setStatuses([]);
    resetToFirstPage();
  }, [resetToFirstPage]);

  const hasActiveFilters = subscriptions.length > 0 || statuses.length > 0;

  return {
    loading,
    filterConfigs,
    filterValues,
    hasActiveFilters,
    handleFilterChange,
    handleClearFilters,
    noDataMessage: hasActiveFilters
      ? "No invoices match these filters"
      : "No invoices yet",
    sorting,
    handleSortingChange,
    pageRows,
    totalCount: filteredInvoices.length,
    pageIndex,
    pageSize,
    handlePageChange,
    invoiceDrawer,
    invoicePdfDownload,
  };
}
